import React, { useContext, useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from 'swiper';
import "swiper/css";
import "swiper/css/navigation";
import styles from "./CarouselSection.module.css";
import { FaQuoteRight } from "react-icons/fa";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { ThemeContext } from "../../../context/ThemeContext";
import logoDark from "../../../logo-dark.svg";
import logoLight from "../../../logo-light.svg";
import logosDark from "../../../assets/images/logos-dark.png";
import logosLight from "../../../assets/images/logos-light.png";
import brasterAvatar from "../../../assets/images/clients/braster.png";
import ludifyAvatar from "../../../assets/images/clients/ludify.png";
import AngeliniMateuszPatorski from "../../../assets/images/clients/Angelini-Mateusz-Patorski.png";
import BrasterArturDrobniak from "../../../assets/images/clients/Braster-Artur-Drobniak.png";
import IvesMateuszFlis from "../../../assets/images/clients/IVES-System-Mateusz-Flis.png";
import SmartmedicsGrzegorzWroblewski from "../../../assets/images/clients/SmartMedics-Grzegorz-Wroblewski.png";
import avatarPlaceholder from "../../../assets/images/avatar.svg";
import { FaTerminal } from "react-icons/fa6";
import { MdKeyboardArrowRight } from "react-icons/md";
import { isSmallScreen } from "../../../utils/screen";
import { useTranslation } from "../../../hooks/useTranslation";

interface CarouselSectionProps {
  showLogos?: boolean;
}

const CarouselSection: React.FC<CarouselSectionProps> = ({ showLogos = true }) => {
  const context = useContext(ThemeContext);
  const swiperRef = useRef<SwiperType>();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { t } = useTranslation();

  const testimonials = [
    {
      text: t("carousel.testimonial.1"),
      author: "Braster",
      name: "Marcin Halicki",
      profileImage: brasterAvatar,
    },
    {
      text: t("carousel.testimonial.2"),
      author: "IVES-system",
      name: "Mateusz Flis",
      profileImage: IvesMateuszFlis,
    },
    {
      text: t("carousel.testimonial.3"),
      author: "Ludify",
      name: "Jakub Oller",
      profileImage: ludifyAvatar,
    },
    {
      text: t("carousel.testimonial.4"),
      author: "SmartMedics Sp. z o.o.",
      name: "Grzegorz Wróblewski",
      profileImage: SmartmedicsGrzegorzWroblewski,
    },
    {
      text: t("carousel.testimonial.5"),
      author: "Angelini Pharma Polska",
      name: "Mateusz Patorski",
      profileImage: AngeliniMateuszPatorski,
    },
    {
      text: t("carousel.testimonial.6"),
      author: "SmartMedics Sp. z o.o.",
      name: "Grzegorz Wróblewski",
      profileImage: SmartmedicsGrzegorzWroblewski,
    },
    {
      text: t("carousel.testimonial.7"),
      author: "Braster",
      name: "Artur Drobniak",
      profileImage: BrasterArturDrobniak,
    },
    {
      text: t("carousel.testimonial.8"),
      author: "Wireless Value",
      name: "Peter Admiraal",
      profileImage: avatarPlaceholder,
    },
    {
      text: t("carousel.testimonial.9"),
      author: "Zign Innovations B.V.",
      name: "Alex van den Heuvel",
      profileImage: avatarPlaceholder,
    },
    {
      text: t("carousel.testimonial.10"),
      author: "Multinational company",
      name: "Karim J.",
      profileImage: avatarPlaceholder,
    },
  ];

  const centerTexts = [
    t("carousel.centerTexts.1"),
    t("carousel.centerTexts.2"),
    t("carousel.centerTexts.3"),
    t("carousel.centerTexts.4"),
    t("carousel.centerTexts.5"),
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!context) {
    throw new Error("CarouselSection must be used within a ThemeProvider");
  }

  const { theme } = context;
  const logo = theme === "dark" ? logoDark : logoLight;
  const logos = theme === "dark" ? logosDark : logosLight;

  const handlePrev = () => {
    if (swiperRef.current?.slidePrev) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current?.slideNext) {
      swiperRef.current.slideNext();
    }
  };

  const handleSlideChange = () => {
    if (swiperRef.current) {
      const activeIndex = swiperRef.current.realIndex;
      const newTextIndex = Math.floor(activeIndex / (isMobile ? 1 : 2)) % centerTexts.length;
      setCurrentTextIndex(newTextIndex);
    }
  };

  const displayedTestimonials = isMobile ? testimonials.slice(0, 4) : testimonials;

  return (
    <section className={styles.carouselSection}>
      {/* Header */}
      <div className={styles.topContainer}>
        <div className={styles.titleContainer}>
          <FaTerminal size={isSmallScreen() ? 44 : 54} />
          <h2 className={styles.title}>{t("carousel.title")}</h2>
        </div>
        <div className={styles.reviews}>
          <p className={styles.reviewsTitle}>{t("carousel.reviews.title")}</p>
          <span className={styles.reviewsTitle}>4.9</span>
          <span className={styles.reviewsStars}>★★★★★</span>
          <br />
          <a href="https://clutch.co/profile/appotype?utm_source=widget&utm_medium=3&utm_campaign=widget&utm_content=stars&utm_term=appotype.eu#logo"
            target="_blank"
            className={styles.reviewLink} rel="noreferrer">
            <span className={styles.linkText}>{t("carousel.reviews.check")}</span>
            <MdKeyboardArrowRight size={24} />
          </a>
        </div>
      </div>

      {/* Navigation Container */}
      <div className={styles.navContainer}>
        <button
          className={`${styles.navArrow} ${styles.navArrowLeft}`}
          onClick={handlePrev}
        >
          <IoChevronBack size={24} />
        </button>
        <div className={styles.centerWidget}>
          <img src={logo} alt="Appotype Logo" className={styles.appotypeLogo} />
          <span>{centerTexts[currentTextIndex]}</span>
        </div>
        <button
          className={`${styles.navArrow} ${styles.navArrowRight}`}
          onClick={handleNext}
        >
          <IoChevronForward size={24} />
        </button>
      </div>

      {/* Swiper Carousel */}
      <Swiper
        modules={[Navigation]}
        onBeforeInit={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={handleSlideChange}
        spaceBetween={30}
        slidesPerView={1}
        loop={true}
        breakpoints={{
          768: {
            slidesPerView: 2,
            slidesPerGroup: 2
          }
        }}
        className={styles.carousel}
      >
        {displayedTestimonials.map((testimonial, index) => (
          <SwiperSlide key={index}>
            <div className={styles.testimonialCard}>
              <FaQuoteRight className={styles.quoteIcon} size={36} />
              <p className={styles.text}>{testimonial.text}</p>
              <div className={styles.profile}>
                <img
                  src={testimonial.profileImage}
                  alt={`${testimonial.name}`}
                  className={styles.profileImage}
                />
                <div>
                  <p className={styles.author}>{testimonial.author}</p>
                  <p className={styles.name}>{testimonial.name}</p>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Client Logos */}
      {showLogos && (
        <div className={styles.logosContainer}>
          <div className={styles.logosTrack}>
            <div className={styles.logosContent}>
              <img src={logos} alt="Client logos" className={styles.logos} />
              <img src={logos} alt="Client logos" className={styles.logos} />
              <img src={logos} alt="Client logos" className={styles.logos} />
              <img src={logos} alt="Client logos" className={styles.logos} />
              <img src={logos} alt="Client logos" className={styles.logos} />
              <img src={logos} alt="Client logos" className={styles.logos} />
              <img src={logos} alt="Client logos" className={styles.logos} />
              <img src={logos} alt="Client logos" className={styles.logos} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CarouselSection;
